import type { JSX } from 'react';
import type { WireArtifact } from '@logistics/contracts';
import { shortDay } from '@/lib/format';
import { Truncated, caption } from './parts';

type Board = Extract<WireArtifact, { kind: 'board' }>;
type Column = Board['columns'][number];
type LoadRef = Column['loads'][number];

/**
 * The loads as a board, one column per status the provider used. Columns share
 * the panel's width and never shrink below reading width: one column fills it,
 * and enough of them scroll sideways instead.
 */
export function BoardArtifact({ artifact }: { artifact: Board }): JSX.Element {
  return (
    <div className="flex flex-col gap-2.5">
      <div className="-mx-[18px] flex gap-2.5 overflow-x-auto px-[18px] pb-1.5">
        {artifact.columns.map((column, i) => (
          <BoardColumn key={i} column={column} />
        ))}
      </div>
      {artifact.columns.length === 0 && (
        <p className="text-mute text-[13px]">No load carried a status.</p>
      )}
      {artifact.truncated && <Truncated what="loads on the board" />}
    </div>
  );
}

function BoardColumn({ column }: { column: Column }): JSX.Element {
  return (
    <div className="border-line bg-surf flex min-w-[188px] flex-1 flex-col gap-2 rounded-xl border p-2.5">
      <div className="flex items-center justify-between gap-2">
        <span className={`${caption} min-w-0 truncate`}>{column.status.toUpperCase()}</span>
        <span className="bg-surf2 text-mute flex-none rounded-full px-2 py-0.5 font-mono text-[10px] tabular-nums">
          {column.count}
        </span>
      </div>
      {column.loads.map((load, i) => (
        <BoardCard key={i} load={load} />
      ))}
    </div>
  );
}

function BoardCard({ load }: { load: LoadRef }): JSX.Element {
  return (
    <div className="bg-surf2 rounded-[10px] px-2.5 py-2">
      <div className="truncate font-mono text-[12px] font-medium">
        #{load.reference.replace(/^#/, '')}
      </div>
      {load.customer !== undefined && (
        <div className="text-mute mt-0.5 truncate text-[11.5px]">{load.customer}</div>
      )}
      <Leg label="PU" place={load.pickup} day={load.pickupDay} />
      <Leg label="DE" place={load.delivery} day={load.deliveryDay} />
    </div>
  );
}

function Leg({
  label,
  place,
  day,
}: {
  label: string;
  place: string | undefined;
  day: string | undefined;
}): JSX.Element | null {
  if (place === undefined && day === undefined) return null;
  return (
    <div className="mt-1 flex items-baseline gap-1.5">
      <span className="text-acc flex-none font-mono text-[9.5px] tracking-[.08em]">{label}</span>
      <span className="min-w-0 truncate text-[11.5px]">{place ?? '—'}</span>
      {day !== undefined && (
        <span className="text-mute flex-none font-mono text-[9.5px] tabular-nums">
          {shortDay(day)}
        </span>
      )}
    </div>
  );
}
